"use client";

import type { ErrorCode, GenerateSttParams } from "@/app/_api/generateStt";
import type { Media } from "@mw/shared/payload-types";
import type { ReactElement, ReactNode } from "react";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { DEFAULT_ERROR, STT_ERROR_MAP, generateSttFromUrl } from "@/app/_api/generateStt";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { toast } from "@/app/_providers/toast";
import React, { createContext, useContext } from "react";
import { create } from "zustand";
import { combine } from "zustand/middleware";

import type { GlobalSTT } from "./STT";
import type { Segment, TranscriptionResponse } from "./types";

export interface TranscriptionResponseWithSegments extends TranscriptionResponse {
  segments: Segment[];
}

import { getSTTDataFromPayload } from "./STTServerFn";
import { urlToFilename } from "./utils";
import { convertSampleResponseToSegments } from "./utils";

export interface ToolItem {
  id: string;
  label: string;
}

export interface TranscriptionItem {
  id: string;
  label: string;
}

interface BaseTabData {
  abortController?: AbortController | null;
  icon?: Media | ReactElement | string;
  id: string;
  isTranscribing: boolean;
}

export interface ToolTabData extends BaseTabData {
  componentId: "url-input-tool" | "home-navigation";
  urlInputTexts?: Record<string, unknown>;
}

export interface TranscriptionTabData extends BaseTabData {
  componentId: "transcription-viewer";
  sourceTitle?: string;
  transcription?: TranscriptionResponseWithSegments;
  uri?: string;
}

export type TabData = ToolTabData | TranscriptionTabData;

/** Same payload shape as STTHero (`mw_stt_example_clicked`): only transcription samples, not URL/tool tabs. */
export function getSttDemoExampleClickedPayload(tabId: string, transcriptions: TranscriptionItem[]) {
  const exampleIndex = transcriptions.findIndex(t => t.id === tabId);
  if (exampleIndex < 0) return null;
  return { example_index: exampleIndex };
}

/** Matches STTHero `getSttHeroContentAnalyticsProps` shapes for play/download; null when no comparable hero context. */
export function getSttDemoContentAnalyticsProps(params: {
  selectedTabId: string | null;
  transcriptions: TranscriptionItem[];
  globalSTT: GlobalSTT;
}) {
  const { selectedTabId, transcriptions, globalSTT } = params;
  if (!selectedTabId) {
    if (globalSTT.file != null || globalSTT.transcription != null) {
      return { content_type: "user_upload" as const };
    }
    return null;
  }
  const exampleIndex = transcriptions.findIndex(t => t.id === selectedTabId);
  if (exampleIndex >= 0) {
    return { content_type: "example" as const, example_index: exampleIndex };
  }
  return null;
}

interface STTState {
  cache: STTCache;
  currentTime: number;
  globalSTT: GlobalSTT;
  isPlaying: boolean;
  isRealtimeEnabled: boolean;
  mediaElement: HTMLAudioElement | null;
  selectedTabId: null | string;
  showLimitReachedOverlay: boolean;
  sttGenerator: ((props: GenerateSttParams) => Promise<TranscriptionResponse>) | null;
  tabData: Record<string, TabData>;
  tools: ToolItem[];
  transcriptions: TranscriptionItem[];
  variant?: "default" | "simple";
}

// Helper functions outside the store
const handleError = (
  error: unknown,
  set: (partial: ((state: STTState) => Partial<STTState>) | Partial<STTState>) => void,
): never => {
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: string }).status in STT_ERROR_MAP
  ) {
    const status = (error as { status: string }).status;
    const errorConfig = STT_ERROR_MAP[status as ErrorCode];
    if (status === "quota_exceeded") {
      captureEvent(EventName.mw_stt_quota_exceeded, { source: "stt_demo" });
      set({ showLimitReachedOverlay: true });
    } else {
      toast.error(errorConfig.title, { description: errorConfig.description });
    }
    captureEvent(EventName.marketing_website_interactive_demo_error, {
      error_status: status,
      product: "stt",
    });
  } else {
    toast.error(DEFAULT_ERROR.title, {
      description: DEFAULT_ERROR.description,
    });
  }
  throw error;
};

const transcribeFileWithGenerator = async (
  file: File,
  abortSignal: AbortSignal,
  sttGenerator: (props: GenerateSttParams) => Promise<TranscriptionResponse>,
  cache: STTCache,
) => {
  // Temp cache for testing
  const cached = await cache.get({ url: file.name });
  if (cached) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      transcription: cached,
      uri: URL.createObjectURL(file),
    };
  }

  const token = await getCaptchaToken({ behaviourType: "stt" });

  const response = await sttGenerator({
    file,
    diarize: true,
    signal: abortSignal,
    hcaptcha_token: token,
  });

  const transcriptionWithSegments: TranscriptionResponseWithSegments = {
    ...response,
    segments: convertSampleResponseToSegments(response),
  };

  return {
    transcription: transcriptionWithSegments,
    uri: URL.createObjectURL(file),
  };
};

// Store factory function that creates a store with initial data
const createSTTStore = (initialData: {
  cache: STTCache;
  globalSTT: GlobalSTT;
  isRealtimeEnabled: boolean;
  selectedTabId: null | string;
  sttGenerator: (props: GenerateSttParams) => Promise<TranscriptionResponse>;
  tabData: Record<string, TabData>;
  tools: ToolItem[];
  transcriptions: TranscriptionItem[];
  variant?: "default" | "simple";
}) => {
  return create(
    combine(
      // Initial state with provided data
      {
        currentTime: 0,
        isRealtimeEnabled: initialData.isRealtimeEnabled,
        globalSTT: initialData.globalSTT,
        isPlaying: false,
        mediaElement: null,
        selectedTabId: initialData.selectedTabId,
        showLimitReachedOverlay: false,
        sttGenerator: initialData.sttGenerator,
        tabData: initialData.tabData,
        tools: initialData.tools,
        transcriptions: initialData.transcriptions,
        cache: initialData.cache,
        variant: initialData.variant,
      } as STTState,
      // Actions
      (set, get) => ({
        actions: {
          resetGlobalSTT: () => {
            set(state => {
              return {
                globalSTT: {
                  ...state.globalSTT,
                  file: null,
                  isTranscribing: false,
                  abortController: null,
                  uri: undefined,
                  transcription: undefined,
                },
                selectedTabId: null,
              };
            });
          },
          updateTabData: (tabId: string, updates: Partial<Pick<BaseTabData, "abortController" | "isTranscribing">>) => {
            set(state => ({
              tabData: {
                ...state.tabData,
                [tabId]: {
                  ...state.tabData[tabId],
                  ...updates,
                } as TabData,
              },
            }));
          },

          // Media element event handlers
          handleMediaPlay: () => {
            set({ isPlaying: true });
          },

          handleMediaPause: () => {
            set({ isPlaying: false });
          },

          handleMediaEnded: () => {
            set({ isPlaying: false });
          },

          handleMediaTimeUpdate: () => {
            const { mediaElement } = get();
            if (mediaElement) {
              set({ currentTime: mediaElement.currentTime });
            }
          },

          setSelectedTabId: (tabId: null | string) => {
            set({ selectedTabId: tabId });
          },

          setSelectedTabIdWithAutoTranscribe: async (tabId: null | string) => {
            const { tabData } = get();

            if (!tabId) {
              set({ selectedTabId: null });
              return;
            }

            const currentTabData = tabData[tabId];

            if (!currentTabData) {
              return;
            }

            // tool tab
            if (currentTabData.componentId !== "transcription-viewer") {
              set({ selectedTabId: tabId });
            } else {
              // Check if transcription already has data
              if (!currentTabData.transcription && currentTabData.uri) {
                const uri = currentTabData.uri;
                const cached = await get().cache.get({ id: currentTabData.id, url: uri });
                if (cached) {
                  set(state => ({
                    tabData: {
                      ...state.tabData,
                      [tabId]: {
                        ...state.tabData[tabId],
                        transcription: cached,
                      },
                    },
                    selectedTabId: tabId,
                  }));
                  return;
                }
                // generate transcription
                const abortController = new AbortController();
                set(state => ({
                  tabData: {
                    ...state.tabData,
                    [tabId]: {
                      ...state.tabData[tabId],
                      isTranscribing: true,
                      abortController,
                    },
                  },
                }));

                // We need to call generateSttFromUrl directly to get the raw response

                const token = await getCaptchaToken({ behaviourType: "stt" });
                const response = await generateSttFromUrl({
                  url: uri,
                  signal: abortController.signal,
                  hcaptcha_token: token,
                });
                if (abortController.signal.aborted) {
                  set(state => ({
                    tabData: {
                      ...state.tabData,
                      [tabId]: {
                        ...state.tabData[tabId],
                        isTranscribing: false,
                        abortController: null,
                      },
                    },
                  }));
                  return;
                }

                const transcriptionWithSegments: TranscriptionResponseWithSegments = {
                  ...response.chunk,
                  segments: convertSampleResponseToSegments(response.chunk),
                };

                set(state => ({
                  tabData: {
                    ...state.tabData,
                    [tabId]: {
                      ...state.tabData[tabId],
                      isTranscribing: false,
                      abortController: null,
                      transcription: transcriptionWithSegments,
                      sourceTitle: response.source_title,
                      uri: response.url,
                    },
                  },
                }));
              }
              set({ selectedTabId: tabId });
            }
          },

          setGlobalSTT: (globalSTT: GlobalSTT) => {
            set({ globalSTT });
          },

          setMediaElement: (mediaElement: HTMLAudioElement | null) => {
            const currentMediaElement = get().mediaElement;

            // Define event handlers inline
            const handlePlay = () => {
              set({ isPlaying: true });
              const s = get();
              const props = getSttDemoContentAnalyticsProps({
                selectedTabId: s.selectedTabId,
                transcriptions: s.transcriptions,
                globalSTT: s.globalSTT,
              });
              if (!props) return;
              captureEvent(EventName.mw_stt_example_played, props);
            };
            const handlePause = () => set({ isPlaying: false });
            const handleEnded = () => set({ isPlaying: false });
            const handleTimeUpdate = () => {
              const element = get().mediaElement;
              if (element) {
                set({ currentTime: element.currentTime });
              }
            };

            // Clean up previous listeners if they exist
            if (currentMediaElement) {
              currentMediaElement.removeEventListener("play", handlePlay);
              currentMediaElement.removeEventListener("pause", handlePause);
              currentMediaElement.removeEventListener("ended", handleEnded);
              currentMediaElement.removeEventListener("timeupdate", handleTimeUpdate);
            }

            // Set up new listeners if mediaElement is provided
            if (mediaElement) {
              mediaElement.addEventListener("play", handlePlay);
              mediaElement.addEventListener("pause", handlePause);
              mediaElement.addEventListener("ended", handleEnded);
              mediaElement.addEventListener("timeupdate", handleTimeUpdate);

              // Set initial state
              set({
                mediaElement,
                isPlaying: !mediaElement.paused,
                currentTime: mediaElement.currentTime,
              });
            } else {
              set({
                mediaElement: null,
                isPlaying: false,
                currentTime: 0,
              });
            }
          },

          setShowLimitReachedOverlay: (show: boolean) => {
            set({ showLimitReachedOverlay: show });
          },

          setTools: (tools: ToolItem[]) => {
            set({ tools });
          },

          setTranscriptions: (transcriptions: TranscriptionItem[]) => {
            set({ transcriptions });
          },

          setTabData: (tabData: Record<string, TabData>) => {
            set({ tabData });
          },

          setSttGenerator: (sttGenerator: (props: GenerateSttParams) => Promise<TranscriptionResponse>) => {
            set({ sttGenerator });
          },

          playMedia: () => {
            const { mediaElement } = get();
            if (!mediaElement) return;
            mediaElement.play();
          },

          pauseMedia: () => {
            const { mediaElement } = get();
            if (!mediaElement) return;
            mediaElement.pause();
          },

          globalTranscribeFile: (file: File) => {
            if (!file) return;

            const { sttGenerator } = get();
            if (!sttGenerator) {
              throw new Error("STT generator not initialized");
            }

            captureEvent(EventName.mw_stt_transcribe_file);

            set({ selectedTabId: null });

            const abortController = new AbortController();
            const currentGlobalSTT = get().globalSTT;
            if (currentGlobalSTT.abortController) {
              currentGlobalSTT.abortController.abort();
            }

            set({
              globalSTT: {
                ...currentGlobalSTT,
                file,
                isTranscribing: true,
                abortController,
              },
            });

            transcribeFileWithGenerator(file, abortController.signal, sttGenerator, get().cache)
              .then(({ transcription, uri }) => {
                if (abortController.signal.aborted) {
                  throw new Error("Aborted");
                }

                set(state => ({
                  globalSTT: {
                    ...state.globalSTT,
                    transcription,
                    uri,
                    isTranscribing: false,
                  },
                }));
              })
              .catch(e => {
                captureEvent(EventName.mw_stt_transcribe_file_error, {
                  transcribe_error: e,
                });
                // We display the error only if the transcription is not aborted
                if (!abortController.signal.aborted) {
                  handleError(e, set);
                }
                // reset global state
                set(state => ({
                  globalSTT: {
                    ...state.globalSTT,
                    isTranscribing: false,
                    file: null,
                    abortController: null,
                    transcription: undefined,
                  },
                }));
              });
          },

          createNewTabFromUrlTranscription: async (
            url: string,
            newTabData: {
              icon?: Media | ReactElement | string;
              name?: string;
            },
          ) => {
            const { selectedTabId, tabData } = get();

            // 1. Set current tab as transcribing
            const currentTabId = selectedTabId;
            const abortController = new AbortController();
            if (currentTabId && tabData[currentTabId]) {
              set(state => ({
                tabData: {
                  ...state.tabData,
                  [currentTabId]: {
                    ...state.tabData[currentTabId],
                    isTranscribing: true,
                    abortController,
                  },
                },
              }));
            }

            captureEvent(EventName.mw_stt_transcribe_url, {
              url,
            });

            try {
              const token = await getCaptchaToken({ behaviourType: "stt" });
              const response = await generateSttFromUrl({ url, signal: abortController.signal, hcaptcha_token: token });

              // Create transcription with segments
              const transcriptionWithSegments: TranscriptionResponseWithSegments = {
                ...response.chunk,
                segments: convertSampleResponseToSegments(response.chunk),
              };

              // Create new transcription and tab data
              const newTabId = `url-${Date.now()}`;
              const urlName = url.split("/").pop()?.split("?")[0] || "URL Transcript";
              const transcriptionName = response.source_title || newTabData.name || urlName;

              const newTranscription: TranscriptionItem = {
                id: newTabId,
                label: transcriptionName,
              };

              const newTabDataEntry: TranscriptionTabData = {
                componentId: "transcription-viewer",
                id: newTabId,
                icon: newTabData.icon,
                isTranscribing: false,
                transcription: transcriptionWithSegments,
                sourceTitle: response.source_title,
                uri: response.url,
              };

              set(state => ({
                transcriptions: [newTranscription, ...state.transcriptions],
                tabData: {
                  ...state.tabData,
                  [newTabId]: newTabDataEntry,
                },
                selectedTabId: newTabId,
              }));
            } catch (error) {
              if (!abortController.signal.aborted) {
                captureEvent(EventName.mw_stt_transcribe_url_error, {
                  transcribe_url: url,
                  transcribe_error: error,
                });
                handleError(error, set);
              }
            } finally {
              if (currentTabId && tabData[currentTabId]) {
                set(state => ({
                  tabData: {
                    ...state.tabData,
                    [currentTabId]: {
                      ...state.tabData[currentTabId],
                      isTranscribing: false,
                      abortController: null,
                    },
                  },
                }));
              }
            }
          },

          startTranscribing: async () => {
            // Implementation when needed
          },
        },
      }),
    ),
  );
};

// Type for the store
type STTStore = ReturnType<typeof createSTTStore>;

// Context for the store
const STTStoreContext = createContext<STTStore | null>(null);

export class STTCache {
  private cache: Record<string, TranscriptionResponseWithSegments | undefined> = {};

  constructor({ cache }: { cache: Record<string, TranscriptionResponseWithSegments | undefined> }) {
    this.cache = cache;
  }

  async get({ id, url }: { id?: string; url: string }): Promise<TranscriptionResponseWithSegments | undefined> {
    const filename = urlToFilename(url);
    if (filename in this.cache) {
      const out = this.cache[filename] ? this.cache[filename] : await getSTTDataFromPayload({ id, name: filename });
      if (out) {
        this.cache[filename] = out;
      } else {
        delete this.cache[filename];
      }
      return out;
    }
    return undefined;
  }

  set(url: string, response: TranscriptionResponseWithSegments) {
    const filename = urlToFilename(url);
    this.cache[filename] = response;
  }
}

interface STTStoreProviderProps {
  cache: STTCache;
  children: ReactNode;
  initialGlobalSTT: GlobalSTT;
  initialSelectedtab: null | string;
  isRealtimeEnabled: boolean;
  sttGenerator: (props: GenerateSttParams) => Promise<TranscriptionResponse>;
  tabData: Record<string, TabData>;
  tools: ToolItem[];
  transcriptions: TranscriptionItem[];
  variant?: "default" | "simple";
}

export function STTStoreProvider({
  children,
  sttGenerator,
  tools,
  transcriptions,
  tabData,
  initialGlobalSTT,
  initialSelectedtab,
  cache,
  variant,
  isRealtimeEnabled,
}: STTStoreProviderProps) {
  // Create store instance with initial data - runs immediately, no useEffect!
  const store = React.useMemo(
    () =>
      createSTTStore({
        tools,
        transcriptions,
        tabData,
        selectedTabId: initialSelectedtab,
        globalSTT: initialGlobalSTT,
        sttGenerator,
        cache,
        variant,
        isRealtimeEnabled,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return <STTStoreContext.Provider value={store}>{children}</STTStoreContext.Provider>;
}

// Hook to get the store from context
const useSTTStore = () => {
  const store = useContext(STTStoreContext);
  if (!store) {
    throw new Error("useSTTStore must be used within STTStoreProvider");
  }
  return store;
};

// Atomic selector hooks for state (following TkDodo's pattern)
export const useSTTSelectedTabId = () => {
  const store = useSTTStore();
  return store(state => state.selectedTabId);
};

export const useSTTSelectedTabData = () => {
  const store = useSTTStore();
  return store(state => (state.selectedTabId ? state.tabData[state.selectedTabId] || null : null));
};

export const useSTTGlobalSTT = () => {
  const store = useSTTStore();
  return store(state => state.globalSTT);
};

export const useSTTTools = () => {
  const store = useSTTStore();
  return store(state => state.tools);
};

export const useSTTTranscriptions = () => {
  const store = useSTTStore();
  return store(state => state.transcriptions);
};

export const useSTTTabData = () => {
  const store = useSTTStore();
  return store(state => state.tabData);
};

export const useSTTMediaElement = () => {
  const store = useSTTStore();
  return store(state => state.mediaElement);
};

export const useSTTIsPlaying = () => {
  const store = useSTTStore();
  return store(state => state.isPlaying);
};

export const useSTTCurrentTime = () => {
  const store = useSTTStore();
  return store(state => state.currentTime);
};

export const useSTTShowLimitReachedOverlay = () => {
  const store = useSTTStore();
  return store(state => state.showLimitReachedOverlay);
};

// Single hook for all actions (following TkDodo's pattern)
export const useSTTActions = () => {
  const store = useSTTStore();
  return store(state => state.actions);
};

export const useSTTVariant = () => {
  const store = useSTTStore();
  return store(state => state.variant);
};

export const useSTTIsRealtimeEnabled = () => {
  const store = useSTTStore();
  return store(state => state.isRealtimeEnabled);
};

export const useTranscription = () => {
  const store = useSTTStore();
  return store(state =>
    // @ts-expect-error - tabData might not have transcription property for all tab types
    state.selectedTabId ? state.tabData[state.selectedTabId]?.transcription : state.globalSTT.transcription,
  );
};

// Combined hook for components that need multiple values (use sparingly)
export const useSTTContext = () => {
  const selectedTabId = useSTTSelectedTabId();
  const selectedTabData = useSTTSelectedTabData();
  const globalSTT = useSTTGlobalSTT();
  const tools = useSTTTools();
  const transcriptions = useSTTTranscriptions();
  const tabData = useSTTTabData();
  const mediaElement = useSTTMediaElement();
  const isPlaying = useSTTIsPlaying();
  const currentTime = useSTTCurrentTime();
  const showLimitReachedOverlay = useSTTShowLimitReachedOverlay();
  const actions = useSTTActions();
  const variant = useSTTVariant();
  const isRealtimeEnabled = useSTTIsRealtimeEnabled();

  return {
    selectedTabId,
    selectedTabData,
    globalSTT,
    tools,
    transcriptions,
    tabData,
    mediaElement,
    isPlaying,
    currentTime,
    showLimitReachedOverlay,
    variant,
    isRealtimeEnabled,
    actions,
  };
};
